import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineCog , HiChevronDown, HiMenuAlt3, HiX } from 'react-icons/hi';
import { GiHamburgerMenu } from "react-icons/gi";
import './Header.css';
import logo from '../images/logo-cigisdacolor.png';
import { historiqueService } from '../services/historiqueService';
import { formationService } from '../services/formationService';
import { apsisService } from '../services/apsisService';
import { supabase } from '../supabaseClient';
import { IoLanguage } from "react-icons/io5";

function Header({ onNavigate, text, setTranslated, currentPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [historiqueDropdownOpen, setHistoriqueDropdownOpen] = useState(false);
  const [availableEditions, setAvailableEditions] = useState([]);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState("fr"); // default language
  const [defaultLangApplied, setDefaultLangApplied] = useState(false);
  const [formationVisible, setFormationVisible] = useState(true);
  const [apsisVisible, setApsisVisible] = useState(true);
  const sidebarRef = useRef(null);

  useEffect(() => {
    loadAvailableEditions();
    loadVisibilitySettings();

    const handleEditionUpdate = () => {
      setDefaultLangApplied(false);
      loadVisibilitySettings();
    };

    // Listen for updates from Management components
    window.addEventListener('formationSettingsUpdated', loadVisibilitySettings);
    window.addEventListener('apsisSettingsUpdated', loadVisibilitySettings);
    window.addEventListener('editionSettingsUpdated', handleEditionUpdate);

    return () => {
      window.removeEventListener('formationSettingsUpdated', loadVisibilitySettings);
      window.removeEventListener('apsisSettingsUpdated', loadVisibilitySettings);
      window.removeEventListener('editionSettingsUpdated', handleEditionUpdate);
    };
  }, []);

  const loadVisibilitySettings = async () => {
    try {
      // Find the active edition first
      const { data: activeEdition } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();
      
      const year = activeEdition?.year || 2026;

      // Formation visibility
      const formSettings = await formationService.getSettings(year);
      setFormationVisible(formSettings.is_visible);

      // APSIS visibility
      const apsisSettings = await apsisService.getSettings(year);
      setApsisVisible(apsisSettings.is_visible);

      // Default Language
      const { data: editionInfo } = await supabase
        .from('editions')
        .select('default_language')
        .eq('year', year)
        .single();
      
      if (editionInfo?.default_language && editionInfo.default_language !== currentLang && !defaultLangApplied) {
        changeLang(editionInfo.default_language);
        setDefaultLangApplied(true);
      }
    } catch (error) {
      console.error('Error loading visibility settings:', error);
    }
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setMenuOpen(false);
        setHistoriqueDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const loadAvailableEditions = async () => {
    try {
      const editions = await historiqueService.getAvailableEditions();
      setAvailableEditions(editions);
    } catch (error) {
      console.error('Error loading available editions:', error);
      setAvailableEditions([]);
    }
  };

  // Helper to trigger Google Translate widget, with retry if not loaded yet
  const changeLang = (lang, retry = 0) => {
    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = lang;
      select.dispatchEvent(new Event('change'));
      setCurrentLang(lang);
    } else if (retry < 10) {
      setTimeout(() => changeLang(lang, retry + 1), 200);
    }
  };

  // Helper function to check if a nav item is active
  const isActive = (page) => {
    if (page === 'accueil' && (!currentPage || currentPage === 'accueil')) return true;
    if (page === 'historique' && (currentPage === 'historique' || currentPage?.startsWith('historique-'))) return true;
    return currentPage === page;
  };

  // Sidebar nav for mobile/tablet
  const Sidebar = () => (
    <div className={`sidebar-overlay${menuOpen ? ' open' : ''}`}>
      <nav className={`sidebar${menuOpen ? ' open' : ''}`} ref={sidebarRef}>
        <button
          className="sidebar-close"
          aria-label="Fermer le menu"
          onClick={() => {
            setMenuOpen(false);
            setHistoriqueDropdownOpen(false);
          }}
        >
          <HiX size={28} />
        </button>
        <ul className="sidebar-nav-links">
          <li>
            <a href="#" 
               className={isActive('accueil') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('accueil');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>ACCUEIL</a>
          </li>
          <li>
            <a href="#" 
               className={isActive('apropos') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('apropos');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>À PROPOS</a>
          </li>
          <li>
            <a href="#" 
               className={isActive('committee') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('committee');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>COMITÉ CIGSIDA</a>
          </li>
          <li>
            <a href="#" 
               className={isActive('challenge') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('challenge');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>CHALLENGE</a>
          </li>
          {formationVisible && (
            <li>
              <a href="#" 
                 className={isActive('formations') ? 'active' : ''}
                 onClick={e => {
                e.preventDefault();
                onNavigate && onNavigate('formations');
                setMenuOpen(false);
                setHistoriqueDropdownOpen(false);
              }}>FORMATIONS</a>
            </li>
          )}
          <li>
            <a href="#" 
               className={isActive('CongressTopics') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('CongressTopics');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>AXES</a>
          </li>
          {apsisVisible && (
            <li>
              <a href="#" 
                 className={isActive('apsis') ? 'active' : ''}
                 onClick={e => {
                e.preventDefault();
                onNavigate && onNavigate('apsis');
                setMenuOpen(false);
                setHistoriqueDropdownOpen(false);
              }}>ASIDEX</a>
            </li>
          )}
          <li>
            <a href="#" 
               className={isActive('speakers') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('speakers');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>COMITÉS</a>
          </li>
          <li>
            <a href="#" 
               className={isActive('media') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('media');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>MEDIA</a>
          </li>
          {availableEditions.length > 0 && (
            <li className="dropdown">
              <a
                href="#"
                className={`dropdown-toggle ${isActive('historique') ? 'active' : ''}`}
                onClick={e => {
                  e.preventDefault();
                  onNavigate && onNavigate('historique');
                  setHistoriqueDropdownOpen(!historiqueDropdownOpen);
                }}
                aria-expanded={historiqueDropdownOpen}
              >
                HISTORIQUE <HiChevronDown className={`chevron ${historiqueDropdownOpen ? 'open' : ''}`} />
              </a>
              {historiqueDropdownOpen && (
                <ul
                  className="dropdown-menu"
                  style={{
                    position: 'static',
                    boxShadow: 'none',
                    border: 'none',
                    background: 'transparent',
                    opacity: 1,
                    visibility: 'visible',
                    marginTop: 0,
                    maxHeight: '40vh',
                    overflowY: 'auto',
                    textAlign: 'left' // <-- align dropdown list to left
                  }}
                >
                  {availableEditions.map(edition => (
                    <li key={`historique-${edition.year}`}>
                      <a href="#" onClick={e => {
                        e.preventDefault();
                        onNavigate && onNavigate(`historique-${edition.year}`);
                        setHistoriqueDropdownOpen(false);
                        setMenuOpen(false);
                      }}>
                        ÉDITION {edition.year}
                        {edition.theme && (
                          <span style={{ fontSize: '11px', color: '#999', display: 'block' }}>
                            {edition.theme.substring(0, 30)}{edition.theme.length > 30 ? '...' : ''}
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
          <li>
            <a href="#" 
               className={isActive('NosFlyers') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('NosFlyers');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>FLYERS</a>
          </li>
          <li>
            <a href="#" 
               className={isActive('contact') ? 'active' : ''}
               onClick={e => {
              e.preventDefault();
              onNavigate && onNavigate('contact');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>CONTACT</a>
          </li>
          {/* Add language dropdown for mobile/tablet */}
          <li className="dropdown">
            <a
              href="#"
              onClick={e => {
                e.preventDefault();
                setLanguageDropdownOpen(!languageDropdownOpen);
              }}
              className="dropdown-toggle"
            >
              <span style={{ marginRight: 6 }}></span>
              <span style={{ fontSize: '16px', marginRight: 2 }}><IoLanguage /></span>
              <HiChevronDown className={`chevron ${languageDropdownOpen ? 'open' : ''}`} />
            </a>
            {languageDropdownOpen && (
              <ul className="dropdown-menu" style={{ textAlign: 'left' }}>
                {languages.map(lang => (
                  <li key={lang.code}>
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        setLanguageDropdownOpen(false);
                        changeLang(lang.code);
                      }}
                      style={{
                        background: currentLang === lang.code ? "#f0f0f0" : "transparent",
                        ...(lang.code === "ar" ? { fontFamily: "'Tajawal', sans-serif" } : {})
                      }}
                    >
                      <img className="lang-flag" src={lang.flag} alt={lang.name} /> {lang.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li><a href="#" style={{ color:'#ffffff' }} className="participer-btn" onClick={() => {
              onNavigate && onNavigate('participation');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>PARTICIPER</a></li>
          <li><a href="https://iamar.ma/trainings/executive-programs" target="_blank" style={{ color:'#ffffff' }} className="formations-btn" onClick={() => {
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}>INSCRIPTIONS AUX FORMATIONS</a></li>
          <li><a href="#"  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate && onNavigate('gestion'); }}><HiOutlineCog size={20} color='#002b5b' /></a></li>
        </ul>
        <div className="sidebar-actions">
          
          {/* <button
            className="admin-btn"
            onClick={() => {
              onNavigate && onNavigate('gestion');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}
            aria-label="Administration"
            style={{
              background: 'none',
              border: 'none',
              color: '#002b5b',
              cursor: 'pointer',
              fontSize: '24px',
              transition: 'all 0.3s ease',
              marginRight: '10px'
            }}
          >
            <HiOutlineCog />
          </button> */}
          {/* <button
            className="participer-btn"
            onClick={() => {
              onNavigate && onNavigate('participation');
              setMenuOpen(false);
              setHistoriqueDropdownOpen(false);
            }}
          >
            PARTICIPER
          </button> */}
        </div>
      </nav>
    </div>
  );

  const languages = [
    { code: "en", name: "Anglais", flag: "https://flagsapi.com/GB/flat/64.png" },
    { code: "fr", name: "Français", flag: "https://flagsapi.com/FR/flat/64.png" },
    { code: "ar", name: "العربية", flag: "https://flagsapi.com/MA/flat/64.png" },
    { code: "es", name: "Español", flag: "https://flagsapi.com/ES/flat/64.png" },
    { code: "de", name: "Deutsch", flag: "https://flagsapi.com/DE/flat/64.png" },
   
    // { code: "zh-CN", name: "中文", flag: "https://flagsapi.com/CN/flat/64.png" }
  ];

  return (
    <header className="site-header">
      <div className="containerheader header-flex">
        <div className="header-left" onClick={() => onNavigate && onNavigate('accueil')} style={{ cursor: 'pointer' }}>
        </div>

        {/* Hide menu-toggle when sidebar is open */}
        {!menuOpen && (
          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <GiHamburgerMenu  size={24} color='#002b5b'/>
          </button>
        )}

        {/* Desktop nav */}
        <nav className={`header-center`}>
          <ul className="nav-links">
            <li><a href="#" 
                   className={isActive('accueil') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('accueil'); }}>ACCUEIL</a></li>
            <li><a href="#" 
                   className={isActive('apropos') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('apropos'); }}>À PROPOS</a></li>
            <li><a href="#" 
                   className={isActive('committee') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('committee'); }}>COMITÉ CIGSIDA</a></li>
            <li><a href="#" 
                   className={isActive('challenge') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('challenge'); }}>CHALLENGE</a></li>
            {formationVisible && (
              <li><a href="#" 
                     className={isActive('formations') ? 'active' : ''}
                     onClick={e => { e.preventDefault(); onNavigate && onNavigate('formations'); }}>FORMATIONS</a></li>
            )}
            {apsisVisible && (
              <li><a href="#" 
                     className={isActive('apsis') ? 'active' : ''}
                     onClick={e => { e.preventDefault(); onNavigate && onNavigate('apsis'); }}>ASIDEX</a></li>
            )}
            <li><a href="#" 
                   className={isActive('media') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('media'); }}>MEDIA</a></li>
            {availableEditions.length > 0 && (
              <li className="dropdown">
                <a
                  href="#"
                  className={`dropdown-toggle ${isActive('historique') ? 'active' : ''}`}
                  onClick={e => {
                    e.preventDefault();
                    onNavigate && onNavigate('historique');
                    setHistoriqueDropdownOpen(!historiqueDropdownOpen);
                  }}
                >
                  HISTORIQUE <HiChevronDown className={`chevron ${historiqueDropdownOpen ? 'open' : ''}`} />
                </a>
                {historiqueDropdownOpen && (
                  <ul className="dropdown-menu" style={{ textAlign: 'left' }}>
                    {availableEditions.map(edition => (
                      <li key={`historique-${edition.year}`}>
                        <a href="#" onClick={e => {
                          e.preventDefault();
                          onNavigate && onNavigate(`historique-${edition.year}`);
                          setHistoriqueDropdownOpen(false);
                        }}>
                          ÉDITION {edition.year}
                          {edition.theme && (
                            <span style={{ fontSize: '11px', color: '#999', display: 'block' }}>
                              {edition.theme.substring(0, 30)}{edition.theme.length > 30 ? '...' : ''}
                            </span>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
            <li><a href="#" 
                   className={isActive('NosFlyers') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('NosFlyers'); }}>FLYERS</a></li>
            <li><a href="#" 
                   className={isActive('contact') ? 'active' : ''}
                   onClick={e => { e.preventDefault(); onNavigate && onNavigate('contact'); }}>CONTACT</a></li>
            {/* Replace the old langue dropdown with the new language selector */}
            <li className="dropdown">
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  setLanguageDropdownOpen(!languageDropdownOpen);
                }}
                className="dropdown-toggle"
              >
                <span style={{ marginRight: 6 }}></span>
                <span role="img" aria-label="language" style={{ fontSize: '16px', marginRight: 2 }}><IoLanguage /></span>
                <HiChevronDown className={`chevron ${languageDropdownOpen ? 'open' : ''}`} />
              </a>
              {languageDropdownOpen && (
                <ul className="dropdown-menu" style={{ textAlign: 'left' }}>
                  {languages.map(lang => (
                    <li key={lang.code}>
                      <a
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          setLanguageDropdownOpen(false);
                          changeLang(lang.code);
                        }}
                        style={{
                          background: currentLang === lang.code ? "#f0f0f0" : "transparent",
                          ...(lang.code === "ar" ? { fontFamily: "'Tajawal', sans-serif" } : {})
                        }}
                      >
                        <img className="lang-flag" src={lang.flag} alt={lang.name} /> {lang.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            <li><a href="#"  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate && onNavigate('gestion'); }}><HiOutlineCog size={20} color='#002b5b' /></a></li>
          </ul>
        </nav>
        <div className="header-right">
          {/* <button
            className="admin-btn"
            onClick={() => onNavigate && onNavigate('gestion')}
            aria-label="Administration"
            style={{
              background: 'none',
              border: 'none',
              color: '#002b5b',
              cursor: 'pointer',
              marginRight: '15px',
              fontSize: '24px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <HiOutlineCog  />
          </button> */}
          <button
            className="participer2-btn"
            onClick={() => onNavigate && onNavigate('participation')}
          >
            PARTICIPER / ASSISTER AU CONFÉRENCE
          </button>
          <button
            className="participer-btn"
            onClick={() => {
              window.location.hash = 'executive-formations';
              if (onNavigate) onNavigate('executive-formations');
            }}
          >
            INSCRIPTIONS AUX FORMATIONS
          </button>
        </div>
      </div>

      <div className="logo">
        <img src={logo} alt="PLAN-B Logo" onClick={() => onNavigate && onNavigate('accueil')} />
      </div>

      {/* Sidebar for mobile/tablet */}
      <Sidebar />
    </header>
  );
}

export default Header;